import { Box, CircularProgress, Divider, Stack } from "@mui/material";
import BaseModal from "../BaseModal/BaseModal";
import * as Styled from "./EventPollModal.styles";
import { useEffect, useState } from "react";
import { MiniPollIcon, ThankyouHeart } from "../../assets";
import {
  Poll as PollObject,
  EventPollFormat,
  transformPollObjToUserPollFormat,
} from "../CreatedPollModal/PollDataModel";
import {
  getParticipantResponse,
  submitParticipantResponse,
} from "../../services/appsync";

type EventPollModalProps = {
  open: any;
  onClose: any;
  pollObj: PollObject;
  participantEmail: string;
  onSubmit?: (selected: string) => void;
};

const EventPollModal = ({
  open,
  onClose,
  onSubmit,
  pollObj,
  participantEmail,
}: EventPollModalProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [pollData, setPollData] = useState<EventPollFormat>({
    eventId: "",
    pollId: "",
    question: "",
    options: [],
  });

  useEffect(() => {
    const getParticipantCurrentResponse = async (
      eventId: string,
      pollId: string
    ) => {
      try {
        const response = await getParticipantResponse(
          eventId,
          pollId,
          participantEmail
        );
        if (response && response.response) {
          setSelected(response.response);
        }
      } catch (error) {
        console.error("Error getting participant response: ", error);
      }
    };

    if (open) {
      const userPoll = transformPollObjToUserPollFormat(pollObj);
      setPollData(userPoll);
      getParticipantCurrentResponse(userPoll.eventId, userPoll.pollId);
    }
  }, [open, pollObj, participantEmail]);

  const defaultSubtitle = "Select one answer for this poll.";

  const handleOptionClick = async (option: string) => {
    try {
      setSelected(option);
      setLoading(true);

      await submitParticipantResponse(
        pollData.eventId,
        pollData.pollId,
        participantEmail,
        option
      );

      setTimeout(() => {
        setShowThankYou(true);
        setLoading(false);
        if (onSubmit) onSubmit(option);
      }, 1500);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting participant response: ", error);
    }
  };

  return (
    <BaseModal
      desktopWidth={!showThankYou ? "880px" : "400px"}
      open={open}
      onClose={onClose}
    >
      <>
        {!showThankYou ? (
          <>
            <Box
              marginBottom={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <img src={MiniPollIcon} alt="mini poll icon" />
              <Styled.HeaderText>EVENT POLL</Styled.HeaderText>
            </Box>
            <Divider />
            <Styled.PollTitle>{pollData.question}</Styled.PollTitle>
            <Styled.Subtitle>{defaultSubtitle}</Styled.Subtitle>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                my={3}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                {pollData.options.map((option) => {
                  const isSelected = selected === option.key;

                  return (
                    <Box
                      key={option.key}
                      onClick={() => handleOptionClick(option.key)}
                      sx={{
                        padding: "16px 24px",
                        borderRadius: "12px",
                        border: isSelected
                          ? "1px solid #C7B9FF"
                          : "1px solid #C7B9FF",
                        backgroundColor: isSelected ? "#C7B9FF1A" : "#FFFFFF",
                        cursor: "pointer",
                        fontWeight: isSelected ? 700 : "none",
                        textAlign: "center",
                        transition: "all 0.2s ease",
                        boxShadow: isSelected
                          ? " 0px 4px 14px 0px #C7B9FF57"
                          : "none",
                        "&:hover": {
                          border: "2px solid #C7B9FF57",
                          boxShadow: "0px 4px 14px 0px #C7B9FF57",
                        },
                      }}
                    >
                      {option.value}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <img
              src={ThankyouHeart}
              alt="thank you heart"
              style={{ margin: "24px 0px" }}
            />
            <Styled.SubmittedHeaderText>
              Thanks for your input!
            </Styled.SubmittedHeaderText>
            <Styled.SubmittedSubtitleText>
              We’re crunching the data...stay tuned for results
            </Styled.SubmittedSubtitleText>
          </Box>
        )}
      </>
    </BaseModal>
  );
};
export default EventPollModal;
